import { from as rxFrom, merge } from 'rxjs'
import { tap, map as rxMap, filter as rxFilter, mergeMap, catchError } from 'rxjs/operators'
import { Validator } from 'vee-validate'
import { filter, head, map, keys, includes } from 'lodash-es'

import i18n from '../../i18n'
import { sessionService } from '../../service/session-service'
import { geoipService } from '../../service/geoip-service'
import { Session } from '../../model/session-model'

import countryPublic from '../../config/country/country-public'
import countryGov from '../../config/country/country-gov'
import countryNgsc from '../../config/country/country-ngsc'
import localePublic from '../../config/locales/locale-public'
import localeGov from '../../config/locales/locale-gov'
import localeNgsc from '../../config/locales/locale-ngsc'
import envs from '../../common/envs'

export const namespace = 'SESSION'

export const types = {
  SET_MODE: 'setMode',
  SET_USER_LANG: 'setUserLang',
  SET_USER_LOCALE: 'setUserLocale',
  SET_USER_LOCALE_BY_RULE: 'setUserLocaleByRule',
  SET_USER_COUNTRY: 'setUserCountry',
  GET_LANGS: 'getLangs',
  SET_SESSION: 'setSession',
  GET_SESSION_BASE: 'getSessionBase',
  GET_SESSION_AND_SET_TARGET_COUNTRY: 'getSessionAndSetTargetCountry',
  GET_SESSION_WITH_COUNTRY: 'getSessionWithCountry',
  GET_COUNTRY_KEY: 'getCountryKey'
}

const countries = {
  'public': countryPublic,
  'gov': countryGov,
  'ngsc': countryNgsc
}

const locales = {
  'public': localePublic,
  'gov': localeGov,
  'ngsc': localeNgsc
}

const state = {
  mode: 'homepage',
  session: new Session(),
  userLocale: {
    code: 'ko_KR',
    lang: 'ko',
    faqAlias: 'cloud',
    displayName: 'KOR',
    noticeLang: 'KOREAN'
  },
  locales: locales[target],
  userCountry: null,
  country: countries[target]
}

const getters = {
  [types.GET_LANGS]: state => {
    return map(keys(state.locales), langCode => state.locales[langCode].lang)
  },
  [types.GET_COUNTRY_KEY]: state => keys(state.country)
}

const mutations = {
  [types.SET_MODE](state, payload) {
    state.mode = payload
  },
  [types.SET_USER_LOCALE](state, payload) {
    // @TODO payload는 userLocale 객체의 code 값이다. 나중에 payload => { code }로 수정
    let userLocale = !!envs.common.fixed.locale ? envs.common.fixed.locale : payload

    if (userLocale == null) {
      userLocale = window.$cookies.get('userLocale')
    } else {
      window.$cookies.set('userLocale', userLocale, -1, '/', envs.cookie.domain)
    }

    if (userLocale == null) {
      return
    }

    userLocale = state.locales[userLocale]
    if (userLocale == undefined || userLocale == null) {
      userLocale = state.locales.en_US
    }

    state.userLocale = { ...userLocale }
    i18n.locale = state.userLocale.lang
    Validator.localize(state.userLocale.lang)

    const html = document.documentElement
    html.setAttribute('lang', state.userLocale.lang)
  },
  [types.SET_SESSION](state, payload) {
    state.session = new Session(payload)
  },
  [types.SET_USER_COUNTRY](state, { code }) {
    state.userCountry = { ...state.country[code.toLowerCase()] }
  }
}

mutations[types.SET_USER_LANG] = (state, payload) => {
  let locale = filter(state.locales, locale => locale.lang == payload)
  locale = head(locale)
  mutations[types.SET_USER_LOCALE](state, locale.code)
}

const actions = {
  [types.GET_SESSION_BASE]({ commit }) {
    return sessionService
      .getSession$()
      .pipe(tap(response => commit(types.SET_SESSION, response.session)))
      .toPromise()
  },
  [types.GET_SESSION_AND_SET_TARGET_COUNTRY]({ commit, dispatch }, { countryCode }) {
    commit(types.SET_USER_COUNTRY, { code: countryCode })
    return dispatch(types.GET_SESSION_BASE)
  },
  [types.GET_SESSION_WITH_COUNTRY]({ state, commit, dispatch }) {
    const session$ = rxFrom(dispatch(types.GET_SESSION_BASE))
    const isNotMemberFlow$ = session$.pipe(
      rxFilter(sessionRes => sessionRes.session.uuid === null),
      mergeMap(() => {
        const isAssignedUserCountry = !!state.userCountry
        if (isAssignedUserCountry) {
          const cacheGetGeoipToPromise = new Promise((resolve, reject) => resolve({ countryCode: state.userCountry.code, isMember: false }))
          return cacheGetGeoipToPromise
        }
        return geoipService.getGeoip$().pipe(
          rxMap(geoipRes => ({
            countryCode: geoipRes === null || geoipRes.code === null ? state.country.kr.code : geoipRes.code,
            isMember: false
          })),
          catchError(e => new Promise((resolve, reject) => resolve({ countryCode: state.country.kr.code, isMember: false })))
        )
      })
    )
    const isMemberFlow$ = session$.pipe(
      rxFilter(sessionRes => sessionRes.session.uuid !== null),
      rxMap(sessionRes => ({
        countryCode: sessionRes.session.country,
        isMember: true
      }))
    )
    return merge(isNotMemberFlow$, isMemberFlow$)
      .pipe(
        tap(({ countryCode }) => {
          const existUserCountry = !!state.userCountry
          if (existUserCountry === false || (existUserCountry && state.userCountry.code !== countryCode)) {
            commit(types.SET_USER_COUNTRY, { code: countryCode })
          }
        })
      )
      .toPromise()
  },
  [types.SET_USER_LOCALE_BY_RULE]({ state, getters, commit }, { queryLang, countryCode }) {
    /*
    *  언어 설정 RULE
    *    1. to.query.lang 가 있는 경우 => lang 에 따라 언어 설정
    *    2. 쿠키 값이 있는 경우 => 쿠키 값에 따라 언어 설정
    *    3. 국가 정보(멤버 프로필, ip)에 따라 =>
    *      일본 => ja_JP / 한국 => ko_KR / CN => zh_CN / 그외 => en_US
    * */
    const locales = state.locales
    let defaultUserLocale = locales['en_US']
    if (!!queryLang && includes(getters[`${types.GET_LANGS}`], queryLang)) {
      if (queryLang === 'ko') {
        defaultUserLocale = locales['ko_KR']
      } else if (queryLang === 'ja') {
        defaultUserLocale = locales['ja_JP']
      } else if (queryLang === 'zh') {
        defaultUserLocale = locales['zh_CN']
      }
    } else if (window.$cookies.get('userLocale') === null) {
      if (countryCode === 'KR') {
        defaultUserLocale = locales['ko_KR']
      } else if (countryCode === 'JP') {
        defaultUserLocale = locales['ja_JP']
      } else if (countryCode === 'CN') {
        defaultUserLocale = locales['zh_CN']
      }
    } else {
      return
    }
    commit(types.SET_USER_LOCALE, defaultUserLocale.code)
  }
}

export const sessionStore = {
  state,
  getters,
  mutations,
  actions,
  namespace,
  types
}
